import React from 'react';
import { 
  Select as MuiSelect, 
  SelectProps as MuiSelectProps, 
  FormControl, 
  InputLabel, 
  MenuItem, 
  FormHelperText,
  ListSubheader
} from '@mui/material';
import { StyledSelect, StyledFormControl } from './Select.style';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  divider?: boolean;
  group?: string;
}

export interface SelectProps extends Omit<MuiSelectProps, 'value' | 'onChange'> {
  /**
   * The label for the select
   */
  label?: string;
  /**
   * The options for the select
   */
  options: SelectOption[];
  /**
   * The current value
   */
  value?: string | number | string[] | number[];
  /**
   * Callback fired when the value changes
   */
  onChange?: (value: string | number | string[] | number[]) => void;
  /**
   * Whether the field has an error
   */
  error?: boolean;
  /**
   * Helper text to display below the select
   */
  helperText?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Whether the field is disabled
   */
  disabled?: boolean;
  /**
   * Whether the field takes full width
   */
  fullWidth?: boolean;
  /**
   * The size of the select
   */
  size?: 'small' | 'medium';
  /**
   * The variant of the select
   */
  variant?: 'outlined' | 'filled' | 'standard';
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Whether multiple selection is enabled
   */
  multiple?: boolean;
}

export const Select: React.FC<SelectProps> = ({
  label,
  options,
  value,
  onChange,
  error = false,
  helperText,
  required = false,
  disabled = false,
  fullWidth = false,
  size = 'medium',
  variant = 'outlined',
  placeholder,
  multiple = false,
  ...props
}) => {
  const handleChange = (event: any) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  const labelId = `select-label-${Math.random().toString(36).substr(2, 9)}`;

  // Group options by group property
  const groupedOptions = options.reduce((acc, option) => {
    const group = option.group || 'default';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(option);
    return acc;
  }, {} as Record<string, SelectOption[]>);

  const renderOptions = () => {
    const groups = Object.keys(groupedOptions);
    
    if (groups.length === 1 && groups[0] === 'default') {
      // No grouping
      return groupedOptions.default.map((option) => (
        <MenuItem 
          key={option.value} 
          value={option.value}
          disabled={option.disabled}
          divider={option.divider}
        >
          {option.label}
        </MenuItem>
      ));
    }

    // Render with groups
    return groups.map((groupName) => {
      const groupOptions = groupedOptions[groupName];
      
      if (groupName === 'default') {
        return groupOptions.map((option) => (
          <MenuItem 
            key={option.value} 
            value={option.value}
            disabled={option.disabled}
            divider={option.divider}
          >
            {option.label}
          </MenuItem>
        ));
      }

      return [
        <ListSubheader key={`header-${groupName}`}>
          {groupName}
        </ListSubheader>,
        ...groupOptions.map((option) => (
          <MenuItem 
            key={option.value} 
            value={option.value}
            disabled={option.disabled}
            divider={option.divider}
          >
            {option.label}
          </MenuItem>
        ))
      ];
    }).flat();
  };

  return (
    <StyledFormControl
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      error={error}
      disabled={disabled}
      required={required}
    >
      {label && (
        <InputLabel id={labelId}>
          {label}
        </InputLabel>
      )}
      
      <StyledSelect
        labelId={labelId}
        value={value}
        onChange={handleChange}
        label={label}
        multiple={multiple}
        displayEmpty={!!placeholder}
        renderValue={
          placeholder && !value 
            ? () => <span style={{ color: '#999' }}>{placeholder}</span>
            : undefined
        }
        {...props}
      >
        {placeholder && !multiple && (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}
        {renderOptions()}
      </StyledSelect>
      
      {helperText && (
        <FormHelperText>
          {helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
}; 